/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Pressable, Icon, Tooltip } from '@components/atoms'
import { mono } from '@components/systems/backoffice/backoffice.tokens'

import {
	useAppDispatch,
	useAppSelector,
	EditMenuType,
	adminActions,
	AdminStateType,
} from '@redux/index'

interface PropsType {
	readOnly?: boolean
}

/** 메뉴 아이콘 — 헤어라인 사각 96 안에 아이콘 하나. 비어 있으면 점선 사각 + "아이콘 추가" */
export const AdminEditMenuIcon = (props: PropsType) => {
	// redux dispatch
	const reduxDispatch = useAppDispatch()

	// redux state
	const editTempMenu: EditMenuType = useAppSelector(state => state.admin.editTempMenu)
	const editMenuMode: AdminStateType['editMenuMode'] = useAppSelector(
		state => state.admin.editMenuMode,
	)

	// state
	const [isHovering, setIsHovering] = React.useState<boolean>(false)

	// variables
	const readOnly: boolean = props.readOnly ?? false
	const disabled: boolean = editMenuMode === 'update' && editTempMenu.id === ''

	const onOpenModal = () => {
		reduxDispatch(adminActions.MENU_OPEN_MODAL('iconPicker'))
	}

	const onMouseOver = React.useCallback(() => {
		setIsHovering(true)
	}, [])

	const onMouseLeave = React.useCallback(() => {
		setIsHovering(false)
	}, [])

	const onRemoveIcon = () => {
		const newMenu: EditMenuType = {
			...editTempMenu,
			icon: '',
		}

		reduxDispatch(adminActions.EDIT_TEMP_MENU({ ...newMenu }))
	}

	return (
		<View css={style}>
			<span className="createMenuLabel">아이콘</span>

			<View
				className={`menuIconView ${editTempMenu.icon === '' ? 'empty' : ''} ${
					disabled ? 'disabled' : ''
				}`}
			>
				<Tooltip title="아이콘 변경을 원하시면 클릭하세요.">
					<Pressable
						className={`iconButton ${editTempMenu.icon === '' ? 'empty' : ''} ${
							disabled ? 'disabled' : ''
						} ${readOnly ? 'readOnly' : ''}`}
						onClick={onOpenModal}
						onMouseOver={onMouseOver}
						onMouseLeave={onMouseLeave}
						disabled={disabled || readOnly}
					>
						{editTempMenu.icon === '' ? (
							<View className="addIconView">
								<Icon name="plus" className="addIconViewIcon" />
								<span className="addIconViewText">아이콘 추가</span>
							</View>
						) : (
							<Icon name={editTempMenu.icon} className="icon" />
						)}
					</Pressable>
				</Tooltip>

				{editTempMenu.icon !== '' ? (
					<Tooltip title="아이콘 삭제">
						<Pressable
							className="removeIconButton"
							css={{ opacity: isHovering ? 1 : 0.5 }}
							onClick={onRemoveIcon}
							disabled={disabled || readOnly}
						>
							<Icon name="close" className="removeIcon" />
							<span className="removeIconText">삭제</span>
						</Pressable>
					</Tooltip>
				) : null}
			</View>
		</View>
	)
}

const style = css({
	position: 'relative',
	display: 'flex',
	alignItems: 'center',
	gap: 24,
	width: '100%',
	padding: '20px 0',
	borderBottom: `1px solid ${mono.line}`,

	'.createMenuLabel': {
		width: 96,
		flexShrink: 0,
		fontSize: 11,
		letterSpacing: '.18em',
		textTransform: 'uppercase',
		fontWeight: 500,
		color: mono.faint,
	},

	'.menuIconView': {
		position: 'relative',
		display: 'flex',
		alignItems: 'center',
		gap: 16,

		'.iconButton': {
			display: 'flex',
			justifyContent: 'center',
			alignItems: 'center',
			width: 96,
			height: 96,
			padding: 0,
			background: mono.surface,
			border: `1px solid ${mono.line}`,
			borderRadius: 0,
			transition: 'border-color 200ms ease-out',

			'&:hover:not(:disabled)': { borderColor: mono.ink },

			'&.empty': {
				border: `1px dashed ${mono.lineInput}`,
			},

			'.icon': {
				fontSize: 44,
				color: mono.ink,
			},

			'.addIconView': {
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				gap: 4,

				'.addIconViewIcon': { fontSize: 20, color: mono.faint },
				'.addIconViewText': { fontSize: 11, color: mono.faint },
			},
		},

		'&.disabled .iconButton': {
			background: mono.bg,
			'.addIconViewIcon, .addIconViewText': { color: mono.lineInput },
		},

		'.removeIconButton': {
			display: 'flex',
			alignItems: 'center',
			gap: 4,
			height: 30,
			padding: '0 8px 0 4px',
			borderRadius: 2,
			color: mono.dim,
			transition: 'color 200ms ease-out, opacity 200ms ease-out',

			'.removeIcon': { fontSize: 16, color: 'inherit' },
			'.removeIconText': { fontSize: 12, fontWeight: 500 },

			'&:hover:not(:disabled)': { opacity: 1, color: mono.ink },
		},
	},
})
